import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

// 2026 event images featured at the top
const images2026 = [
  "/2026/BBND3425.JPG",
  "/2026/BBND3427.JPG",
  "/2026/BBND3429.JPG",
  "/2026/BBND3435.JPG",
  "/2026/BBND3462.JPG",
  "/2026/BBND3468.JPG",
  "/2026/BBND3477.JPG",
  "/2026/BBND3483.JPG",
  "/2026/BBND3494.JPG",
  "/2026/BBND3513.JPG",
  "/2026/BBND3516.JPG",
  "/2026/BBND3519.JPG",
  "/2026/BBND3526.JPG",
  "/2026/BBND3557.JPG",
  "/2026/BBND3570.JPG",
  "/2026/BBND4121.JPG",
  "/2026/BBND5234.JPG",
  "/2026/BBND5219.JPG",
];

// Existing gallery collection
const existingGalleryImages = [
  "/images/gallery/img_1.jpg",
  "/images/gallery/img_2.JPG",
  "/images/gallery/img_3.JPG",
  "/images/gallery/img_4.JPG",
  "/images/gallery/img_5.JPG",
  "/images/gallery/img_6.JPG",
  "/images/gallery/img_7.JPG",
  "/images/gallery/img_8.JPG",
  "/images/gallery/img_9.JPG",
  "/images/gallery/img_10.JPG",
  "/images/gallery/img_11.JPG",
  "/images/gallery/img_12.JPG",
  "/images/gallery/img_13.JPG",
  "/images/gallery/img_14.JPG",
  "/images/gallery/img_15.JPG",
  "/images/gallery/img_16.JPG",
  "/images/gallery/img_17.JPG",
  "/images/gallery/img_18.JPG",
  "/images/gallery/img_19.JPG",
  "/images/gallery/img_20.JPG",
  "/images/gallery/img_21.JPG",
  "/images/gallery/img_22.JPG",
  "/images/gallery/img_23.JPG",
  "/images/gallery/img_24.JPG",
  "/images/gallery/img_25.JPG",
  "/images/gallery/img_26.JPG",
  "/images/gallery/img_27.JPG",
  "/images/gallery/img_28.JPG",
  "/images/gallery/img_29.JPG",
  "/images/gallery/img_30.JPG",
  "/images/gallery/img_31.JPG",
  "/images/gallery/img_32.JPG",
  "/images/gallery/img_34.JPG",
  "/images/gallery/img_35.JPG",
  "/images/gallery/img_36.JPG",
  "/images/gallery/img_37.JPG",
  "/images/gallery/img_38.JPG",
  "/images/gallery/img_39.JPG",
  "/images/gallery/img_40.JPG",
];

const images = [...images2026, ...existingGalleryImages];

const Gallery = () => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const scrollRef = useRef(null);

  // Keyboard navigation for modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedIndex === null) return;
      if (e.key === "Escape") setSelectedIndex(null);
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  const totalImages = images.length;

  const nextImage = () => setSelectedIndex((prev) => (prev + 1) % totalImages);
  const prevImage = () => setSelectedIndex((prev) => (prev + totalImages - 1) % totalImages);

  const scrollLeft = () => {
    scrollRef.current.scrollBy({ left: -300, behavior: "smooth" });
  };

  const scrollRight = () => {
    scrollRef.current.scrollBy({ left: 300, behavior: "smooth" });
  };

  return (
    <div className="px-4 sm:px-6 py-8 max-w-7xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-3xl sm:text-4xl font-bold text-brandRed pb-2">
          Image Gallery
        </h1>
        <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
          Explore moments from our community outreach programs, health camps, youth initiatives, and the annual Janhit Mini Marathon.
        </p>
      </div>

      {/* Grid Gallery */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-12">
        {images.map((img, index) => (
          <motion.div
            key={index}
            className="overflow-hidden rounded-xl shadow-md group relative bg-gray-100"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.25 }}
          >
            {index < images2026.length && (
              <span className="absolute top-3 left-3 z-10 bg-brandRed text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-md">
                2026
              </span>
            )}
            <img
              src={img}
              alt={`Gallery item ${index + 1}`}
              loading="lazy"
              decoding="async"
              className="w-full h-64 object-cover cursor-pointer transition-transform duration-300 group-hover:scale-105"
              onClick={() => setSelectedIndex(index)}
            />
          </motion.div>
        ))}
      </div>

      {/* Scrollable Strip */}
      <div className="relative flex items-center">
        <button
          onClick={scrollLeft}
          className="absolute left-0 z-10 bg-brandRed bg-opacity-50 text-brandRed text-md p-2 rounded-full hover:bg-opacity-80"
        >
          &#10094;
        </button>

        <div
          ref={scrollRef}
          className="flex overflow-x-auto gap-4 scroll-smooth py-2 no-scrollbar mx-10"
        >
          {images.map((img, index) => (
            <img
              key={index}
              src={img}
              alt={`Scroll ${index + 1}`}
              loading="lazy"
              decoding="async"
              className="w-40 h-28 object-cover cursor-pointer rounded shadow-md hover:scale-105 transition transform flex-shrink-0"
              onClick={() => setSelectedIndex(index)}
            />
          ))}
        </div>

        <button
          onClick={scrollRight}
          className="absolute right-0 z-10 bg-brandRed bg-opacity-50 text-white text-md p-2 rounded-full hover:bg-opacity-80"
          aria-label="Next slide"
        >
          &#10095;
        </button>
      </div>

      {/* Custom Modal */}
      <AnimatePresence>
        {selectedIndex !== null && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-85 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedIndex(null)}
          >
            <motion.div
              className="relative max-w-4xl max-h-[90vh] w-full flex items-center justify-center"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={images[selectedIndex]}
                alt={`Selected item ${selectedIndex + 1}`}
                className="max-h-[85vh] w-auto max-w-full rounded-lg shadow-2xl object-contain"
              />

              {/* Close Button */}
              <button
                className="absolute top-2 right-2 sm:-top-10 sm:right-0 text-white hover:text-brandRed text-3xl sm:text-4xl font-bold bg-black bg-opacity-50 sm:bg-transparent rounded-full px-3 py-1 transition-colors"
                onClick={() => setSelectedIndex(null)}
                aria-label="Close image preview"
              >
                &times;
              </button>

              {/* Navigation */}
              <button
                className="absolute left-2 sm:-left-12 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-50 hover:bg-opacity-80 p-2 sm:p-3 rounded-full text-xl sm:text-2xl font-bold transition shadow-lg"
                onClick={prevImage}
                aria-label="Previous image"
              >
                &#10094;
              </button>
              <button
                className="absolute right-2 sm:-right-12 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-50 hover:bg-opacity-80 p-2 sm:p-3 rounded-full text-xl sm:text-2xl font-bold transition shadow-lg"
                onClick={nextImage}
                aria-label="Next image"
              >
                &#10095;
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
